import React from 'react';

import { di } from 'react-magnetic-di';
import { shallowEqualObjects } from 'shallow-equal';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import UFOCustomData from '@atlaskit/react-ufo/custom-data';
import UFOSegment from '@atlaskit/react-ufo/segment';
import Suspense from '@atlaskit/react-ufo/suspense';

import { useActiveTabContext } from '../active-tab-provider/useActiveTabContext';
import { ChatHistory, type LiveChatHistoryProps } from '../chat-history';

/**
 * Renders ChatHistory with tab state from ActiveTabProvider.
 * Must be rendered inside ActiveTabProvider.
 */
const ActiveTabAwareChatHistoryComponent = (
	props: Omit<LiveChatHistoryProps, 'onActiveTabChange' | 'activeTabId'>,
): React.JSX.Element => {
	di(ChatHistory);
	const { activeTabId, handleActiveTabChange } = useActiveTabContext();
	return (
		<UFOSegment name={'chat-history'}>
			<>
				{/* for UFO performance tracking dimensions */}
				<UFOCustomData data={{ activeMenu: 'conversation-history' }} />
				<Suspense fallback={<></>} interactionName="active-tab-aware-chat-history">
					<ChatHistory
						{...props}
						onActiveTabChange={handleActiveTabChange}
						activeTabId={activeTabId}
					/>
				</Suspense>
			</>
		</UFOSegment>
	);
};

// Experiment-only: absorb parent render waves when history props are stable.
const MemoizedActiveTabAwareChatHistory = React.memo(
	ActiveTabAwareChatHistoryComponent,
	shallowEqualObjects,
);

export const ActiveTabAwareChatHistory = (
	props: React.ComponentProps<typeof ActiveTabAwareChatHistoryComponent>,
): React.JSX.Element => {
	return FeatureGates.getExperimentValue(
		'rovo_ttai_segment_render_stabilization',
		'isEnabled',
		false,
	) ? (
		<MemoizedActiveTabAwareChatHistory {...props} />
	) : (
		<ActiveTabAwareChatHistoryComponent {...props} />
	);
};
